import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleDetails, siteConfig } from '@/mocks/siteData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articleDetails[id || ''];

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:underline mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        image: article.image,
        articleBody: article.content.replace(/<[^>]*>/g, ''),
        publisher: { '@type': 'Organization', name: siteConfig.name },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, item: { '@id': siteConfig.url, name: '首页' } },
          { '@type': 'ListItem', position: 2, item: { '@id': `${siteConfig.url}/category/${article.categorySlug}`, name: article.category } },
          { '@type': 'ListItem', position: 3, item: { '@id': `${siteConfig.url}/article/${id}`, name: article.title } },
        ],
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main>
        <article className="pt-24 pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-6">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-label font-medium rounded-full mb-4 cursor-pointer hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.category}
              </Link>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-xs"></i></div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-xs"></i></div>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line text-xs"></i></div>
                  {article.views.toLocaleString()} 阅读
                </span>
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            <div className="article-content text-foreground-800 leading-relaxed text-sm md:text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>

            {article.relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {article.relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="flex items-center gap-3 p-4 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-600">
                        <i className="ri-article-line"></i>
                      </div>
                      <span className="text-sm text-foreground-700 font-medium">{ra.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}